export const BACKEND_URL =
  typeof window !== 'undefined' && window.location && window.location.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;

export const STATUS_MAP = {
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  submitted_to_evv: {
    label: 'Submitted to EVV',
    color: 'bg-emerald-100 text-emerald-800',
  },
  claims_created: { label: 'Claims Created', color: 'bg-purple-100 text-purple-800' },
};

const READINESS_BORDER = {
  ready: 'border-l-emerald-500',
  review: 'border-l-amber-500',
};
export const borderClassFor = (editable, readinessLevel) => {
  if (!editable) return 'border-l-slate-200';
  return READINESS_BORDER[readinessLevel] || 'border-l-red-500';
};

const READINESS_BADGE_CLASS = {
  ready: 'bg-emerald-100 text-emerald-700',
  review: 'bg-amber-100 text-amber-700',
};
export const readinessBadgeClass = (level) =>
  READINESS_BADGE_CLASS[level] || 'bg-red-100 text-red-700';

const READINESS_BADGE_LABEL = { ready: '✓ Ready', review: 'Needs review' };
export const readinessBadgeLabel = (level) =>
  READINESS_BADGE_LABEL[level] || 'Blocked';

export const confidenceRowClass = (conf) => {
  if (conf == null) return '';
  if (conf < 60) return 'border-l-4 border-l-red-400 bg-red-50/40';
  if (conf < 80) return 'border-l-4 border-l-amber-400 bg-amber-50/40';
  return '';
};

export const confidenceTextClass = (conf) => {
  if (conf < 60) return 'text-red-600';
  if (conf < 80) return 'text-amber-600';
  return 'text-slate-400';
};

export const dropzoneClass = (uploading, isDragging) => {
  if (uploading) return 'border-indigo-300 bg-indigo-50/60';
  if (isDragging) return 'border-indigo-500 bg-indigo-50';
  return 'border-slate-300 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50/30';
};

export const DAY_ORDER = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
export const UNIT_MINUTES = 15;

// --- Time helpers --------------------------------------------------------
export const timeToMinutes = (t) => {
  if (!t) return null;
  const s = String(t).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  const p = (m[3] || '').toUpperCase();
  if (p === 'AM' && h === 12) h = 0;
  else if (p === 'PM' && h !== 12) h += 12;
  if (Number.isNaN(h) || Number.isNaN(mi)) return null;
  return h * 60 + mi;
};

export const minutesBetween = (tin, tout) => {
  const a = timeToMinutes(tin);
  const b = timeToMinutes(tout);
  if (a === null || b === null) return null;
  return b >= a ? b - a : b + 24 * 60 - a;
};

// Special rule: 35-59 minutes = 3 units (45-min minimum billing)
export const minutesToUnits = (mins) => {
  if (mins === null || mins === undefined || mins <= 0) return 0;
  if (mins >= 35 && mins <= 59) return 3;
  return Math.round(mins / UNIT_MINUTES);
};

export const entryUnits = (entry) => {
  const mins = minutesBetween(entry.time_in, entry.time_out);
  if (mins !== null) return minutesToUnits(mins);
  const h = parseFloat(entry.total_hours) || 0;
  return minutesToUnits(h * 60);
};

export const entryHours = (entry) => {
  const mins = minutesBetween(entry.time_in, entry.time_out);
  if (mins !== null) return mins / 60;
  return parseFloat(entry.total_hours) || 0;
};

// --- Date helpers --------------------------------------------------------
export const parseDateValue = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const year = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
    return new Date(year, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  }
  m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  }
  return null;
};

export const sortEntriesChronologically = (entries) => {
  const copy = [...(entries || [])];
  copy.sort((a, b) => {
    const da = parseDateValue(a.date);
    const db = parseDateValue(b.date);
    if (da && db) return da - db;
    if (da) return -1;
    if (db) return 1;
    const oa = DAY_ORDER[a.day_of_week] ?? 99;
    const ob = DAY_ORDER[b.day_of_week] ?? 99;
    return oa - ob;
  });
  return copy;
};

export const formatWeekRange = (extracted) => {
  const start = extracted?.week_start_date;
  const end = extracted?.week_end_date;
  if (start && end) return `${start} → ${end}`;
  if (start) return `Week starting ${start}`;
  return null;
};

export const FILTER_TABS = [
  { k: 'all', label: 'All' },
  { k: 'pending_review', label: 'Pending Review' },
  { k: 'confirmed', label: 'Confirmed' },
  { k: 'submitted_to_evv', label: 'Submitted to EVV' },
  { k: 'claims_created', label: 'Claims Created' },
];
